'use client';

import { useConstant } from '@cc-shopping/hooks/useConstant';
import { type PropsWithChildren, useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useReferrerContext } from '../../context/referrer';
import { useAnalytics } from '../../hooks/useAnalytics';
import type { PagePayload } from '../../Shibuya';
import { AnalyticsPageViewContext } from './Context';

export interface PageViewProps {
  name?: string;
  contentId?: PagePayload['content_id'];
  contentSource?: PagePayload['content_source'];
  dangerouslyIgnoreCanonicalUrl?: boolean;
}

export const AnalyticsPageView = ({
  name = '',
  contentId,
  contentSource,
  dangerouslyIgnoreCanonicalUrl,
  children,
}: PropsWithChildren<PageViewProps>) => {
  const { analytics, deployableName, deployableVersion, deviceId, gitSha } = useAnalytics();
  const [pageViewReady, setPageViewReady] = useState(false);
  const pageViewId = useConstant(() => uuid());
  const { referrer, isInitialPageLoad, setInitialPageLoaded } = useReferrerContext();

  useEffect(() => {
    if (!analytics) return;
    if (deviceId) {
      const payload: PagePayload = {
        deployable_name: deployableName,
        deployable_version: deployableVersion,
        pageview_id: pageViewId,
        traditional_device_id: deviceId,
        content_id: contentId,
        content_source: contentSource,
        referrer: isInitialPageLoad ? document.referrer : (referrer ?? document.referrer),
      };

      if (dangerouslyIgnoreCanonicalUrl) {
        payload.path = window.location.pathname;
        payload.url = window.location.href;
      }

      const context = {
        app: {
          name: deployableName,
          version: gitSha,
        },
      };

      if (name) {
        analytics.page(name, payload, {
          context,
        });
      } else {
        analytics.page(payload, undefined, {
          context,
        });
      }
      setPageViewReady(true);

      if (isInitialPageLoad) {
        setInitialPageLoaded();
      }
    }
  }, [deviceId]);

  return (
    <AnalyticsPageViewContext.Provider
      value={{ pageName: name, pageViewId, pageViewReady, contentId, contentSource }}
    >
      {children}
    </AnalyticsPageViewContext.Provider>
  );
};
